import React, { useCallback, useState } from 'react';

import { LuChevronDown } from 'react-icons/lu';

import type { CollapsibleSectionProps } from './types';
import { cn } from '../../utils/cn';
import { CollapsiblePanel } from '../CollapsiblePanel';

/**
 * A collapsible component that can be used to hide and show content.
 *
 * @param bgClassNames - The class names to apply to the background of the collapsible.
 * @param buttonTextClosed - The text to display on the button when the collapsible is closed.
 * @param buttonTextOpen - The text to display on the button when the collapsible is open.
 * @param children - The content to be displayed when the collapsible is open.
 * @param defaultOpen - Whether the collapsible starts open in uncontrolled mode.
 * @param headerChildren - Always-visible header actions that do not toggle the panel.
 * @param headerClassNames - Extra class names for the header row.
 * @param headerNotification - The content to display in the header when the collapsible is closed.
 * @param icon - The icon to display in the header.
 * @param keepMounted - When true, children stay mounted while closed.
 * @param onOpenChange - A function to call when the collapsible is opened or closed.
 * @param open - Whether the collapsible is open or not.
 * @param panelClassNames - Extra class names for the collapsing panel.
 * @param title - The title to display in the header.
 * @param titleClassNames - The class names to apply to the title of the collapsible.
 *
 * @example
 * <CollapsibleSection title="Title" buttonTextOpen="Open" buttonTextClosed="Close">
 *   <div>Content</div>
 * </CollapsibleSection>
 *
 * @example
 * <CollapsibleSection title="Title" buttonTextOpen="Open" buttonTextClosed="Close" open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
 *   <div>Content</div>
 * </CollapsibleSection>
 *
 * @example
 * <CollapsibleSection title="Title" defaultOpen headerChildren={<div>Action</div>}>
 *   <div>Content</div>
 * </CollapsibleSection>
 */

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
    'aria-describedby': ariaDescribedBy,
    bgClassNames,
    buttonTextClosed,
    buttonTextOpen,
    children,
    defaultOpen = false,
    headerChildren,
    headerClassNames,
    headerNotification,
    icon,
    keepMounted,
    onOpenChange,
    open,
    panelClassNames,
    style,
    testId,
    title,
    titleClassNames,
}) => {
    const isControlled = open !== undefined && onOpenChange !== undefined;
    const [internalOpen, setInternalOpen] = useState<boolean>(isControlled ? open : defaultOpen);

    const isOpen = isControlled ? open : internalOpen;
    const hasButtonText = Boolean(buttonTextOpen || buttonTextClosed);
    const hasHeaderChildren = headerChildren != null;

    const handleOpen = useCallback(() => {
        if (isControlled) {
            onOpenChange(!isOpen);
        } else {
            setInternalOpen(!isOpen);
        }
    }, [isControlled, onOpenChange, isOpen]);

    const titleBlock = (
        <div className="flex gap-2 justify-between w-full">
            <h6 className={cn('flex gap-2 items-center text-midnight', titleClassNames)}>
                {icon}
                {title}
            </h6>

            {headerNotification != null && (
                <div
                    className={cn(
                        'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
                        isOpen ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
                    )}
                    inert={isOpen}
                >
                    <div className="min-h-0 overflow-hidden">{headerNotification}</div>
                </div>
            )}
        </div>
    );

    const toggleControls = (
        <div
            className={cn(
                'inline-flex items-center justify-end space-x-2 text-xs text-grey',
                hasButtonText && 'min-w-[100px] px-2.5 py-2',
            )}
        >
            {hasButtonText && <span>{isOpen ? buttonTextClosed : buttonTextOpen}</span>}
            <LuChevronDown
                aria-hidden="true"
                className={cn(isOpen ? 'rotate-180' : 'rotate-0', 'w-4 h-4 transition-transform')}
            />
        </div>
    );

    return (
        <div className={cn('px-3 py-2 bg-grey-light', bgClassNames)} data-testid={testId} style={style}>
            {hasHeaderChildren ? (
                <div className={cn('flex w-full items-center gap-2', headerClassNames)}>
                    <button
                        type="button"
                        aria-describedby={ariaDescribedBy}
                        aria-expanded={isOpen}
                        className="flex min-w-0 flex-1 items-center cursor-pointer text-left"
                        onClick={handleOpen}
                    >
                        {titleBlock}
                    </button>
                    <div className="flex items-center justify-end flex-wrap gap-1.5">{headerChildren}</div>
                    <button
                        type="button"
                        aria-hidden="true"
                        tabIndex={-1}
                        className="shrink-0 cursor-pointer"
                        onClick={handleOpen}
                    >
                        {toggleControls}
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    aria-describedby={ariaDescribedBy}
                    aria-expanded={isOpen}
                    className={cn('flex w-full items-center justify-between cursor-pointer', headerClassNames)}
                    onClick={handleOpen}
                >
                    {titleBlock}
                    <div className="flex items-center gap-1.5">{toggleControls}</div>
                </button>
            )}
            <CollapsiblePanel className={panelClassNames} isOpen={isOpen} keepMounted={keepMounted}>
                {children}
            </CollapsiblePanel>
        </div>
    );
};
